import api from 'src/config/api';
import { Pagination } from 'src/types/services';
import {
  PolicyTypesResponse,
  ResponseDetailPolicyTypes,
  ResponseRequestTimeOff,
  Vacations,
  ApprovalStepsType,
  VacationsNotificationsResponse,
  ResponseBalanceReport,
  AdjustAvailableTimeParams,
  BalanceConfomationResponse,
  VacationStatus,
  TimeOffRequestedDaysResponse,
  ResponseRequestCalendar,
} from 'src/types/vacations';
import { UserListResponse } from 'src/types/user';

export const getPolicyTypesByUserId = (userId: number) =>
  api.get<PolicyTypesResponse>('/vacations/policy-types', {
    params: {
      userId,
    },
  });

export const getPolicyTypes = () =>
  api.get<PolicyTypesResponse>('/vacations/policy-types');

export const getVacationsRequestCollaborator = (policyTypeId, page, limit) =>
  api.get<Pagination<Vacations>>('/vacations/requests', {
    params: {
      policyTypeId,
      role: 'COLLABORATOR',
      page,
      limit,
    },
  });

export const getVacationsRequestManager = (policyTypeId, userIds, page, limit) =>
  api.get<Pagination<Vacations>>('/vacations/requests', {
    params: {
      policyTypeId,
      userIds,
      role: 'MANAGER',
      page,
      limit,
    },
  });

export const createVacationRequest = ({
  policyTypeId,
  fromDate,
  toDate,
  description,
  attachments,
}) =>
  api.post<ResponseRequestTimeOff>('/vacations/requests', {
    policyTypeId,
    fromDate,
    toDate,
    description,
    attachments,
  });

export const editFilesInRequest = ({ id, attachments }) =>
  api.put<ResponseRequestTimeOff>(`/vacations/requests/${id}/attachments`, {
    attachments,
  });


export const approveOrRejectRequest = ({
  requestId,
  stepId,
  state,
}) =>
  api.put(`/vacations/requests/${requestId}/approval-steps/${stepId}/state`, {
    state,
  });

export const getVacationsDetail = (id) =>
  api.get<ResponseRequestTimeOff>(`/vacations/requests/${id}`);

export const getPolicyTypeDetail = (policyTypeId, userIdCollaborator = undefined) =>
  api.get<ResponseDetailPolicyTypes>(
    `/vacations/policy-types/${policyTypeId}`, {
    params: {
      userId: userIdCollaborator,
    },
  });

export const getApprovalStep = (id) =>
  api.get<ApprovalStepsType>(`/vacations/requests/${id}/approval-steps`);

export const getApprovalStepApprovers = (
  {
    vacationId,
    stepId,
    page,
    limit,
    orderBy = 'NAME',
    order = 'ASC',
  }
) =>
  api.get<UserListResponse>(`/vacations/requests/${vacationId}/approval-steps/${stepId}/potential-approvers`,
    {
      params: {
        page,
        limit,
        orderBy: orderBy,
        order: order,
      },
    }
  );

// Notifications
export const getVacationsNotifications = () =>
  api.get<VacationsNotificationsResponse>('red-bubbles/vacations');

export const clearVacationsNotifications = (body) =>
  api.post('red-bubbles/vacations/clear', body);

export const getBalanceReport = (
  policyTypeId,
  userIds,
  page,
  limit,
  orderBy = 'NAME',
  order = 'ASC',
) =>
  api.get<ResponseBalanceReport>(`vacations/policy-types/${policyTypeId}/balances`,
    {
      params: {
        page,
        limit,
        userIds,
        orderBy: orderBy,
        order: order,
      },
    }
  );

export const getUserListSubordinatesForVacations = (userId, search, page, limit, filterSelf) =>
  api.get(
    `/organization-charts/${userId}/subordinates`,
    {
      params: {
        search,
        page,
        limit,
        filterSelf,
      },
    });

export const adjustAvailableTime = ({
  policyTypeId,
  userId,
  operation,
  amount,
  observations,
}: AdjustAvailableTimeParams) =>
  api.post(`vacations/policy-types/${policyTypeId}/balances/manual-correction`, {
    userId,
    operation,
    amount,
    observations,
  });

export const getIssuerRequestsList = (search, page, limit, filterSelf) =>
  api.get(
    '/vacations/issuers',
    {
      params: {
        search: search || undefined,
        page,
        limit,
        filterSelf,
      },
    });

export const getEventsOfPolicyTypes = (
  policyTypeId: number,
  page: number,
  limit: number,
  userIdCollaborator: number = undefined
) =>
  api.get<BalanceConfomationResponse>(
    `vacations/policy-types/${policyTypeId}/events`, {
    params: {
      page,
      limit,
      userId: userIdCollaborator,
    },
  });


export const downloadReportBalances = (
  policyTypeId: number,
  fileType: string,
  userIds: string,
) =>
  api.get(
    `vacations/policy-types/${policyTypeId}/balances/report`,
    {
      responseType: 'blob',
      params: {
        fileType,
        userIds: userIds || undefined,
      },
    },
  );

export const downloadReportRequests = (
  policyTypeId: number,
  fileType: string,
  userIds: string,
) =>
  api.get(
    'vacations/requests/report',
    {
      responseType: 'blob',
      params: {
        policyTypeId,
        fileType,
        userIds: userIds || undefined,
      },
    },
  );

export const cancelRequest = (requestId) =>
  api.put(
    `vacations/requests/${requestId}/state`,
    {
      state: VacationStatus.CANCELLED,
    },
  );

export const getRequestedDays = (policyTypeId: number, fromDate: string, toDate: string) =>
  api.post<TimeOffRequestedDaysResponse>('vacations/requests/preview', {
    policyTypeId,
    fromDate,
    toDate,
  });

export const getRequestToCalendar = (
  page: number,
  limit: number,
  month: string,
) =>
  api.get<ResponseRequestCalendar>(
    '/vacations/calendar', {
    params: {
      page,
      limit,
      month,
    },
  });
